import { isProtectedCardProduct, productCardCTA } from '../../../app/Libraries/ProductCardAvailability';
import { nativeWatchCardOptions } from './watch-card-options';
import { escapeMarkup, isWatchType } from './watch-buying-contract';

const SIGNATURE_HOSTS = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca', 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com'];
const collectionPath = (path: string) => /^\/(?:fr\/)?collections\/timepieces(?:\/[a-z0-9-]+)*\/?$/i.test(path)
  && !/rolex|tag[\s_-]*heuer/i.test(path);

export function signatureSansEnabled(doc: Document = document, location: Pick<Location, 'hostname' | 'pathname'> = window.location): boolean {
  return SIGNATURE_HOSTS.includes(location.hostname) && collectionPath(location.pathname)
    && !!doc.querySelector('main[data-signature-sans-cards][data-watch-buying="collection"]');
}

export function signatureVariantId(value: unknown): string {
  const id = String(value || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
  return /^[1-9]\d*$/.test(id) ? `gid://shopify/ProductVariant/${id}` : '';
}

const tagsOf = (hit: any): string[] => Array.isArray(hit?.tags) ? hit.tags.map(tag => String(tag).trim().toLowerCase()) : [];
const online = (hit: any): boolean => {
  const available = hit?.named_tags?.available;
  return tagsOf(hit).includes('available:online') || available === true
    || (Array.isArray(available) ? available : [available]).some(value => String(value || '').toLowerCase() === 'online');
};

export type SignatureCardStock = 'in-stock' | 'out-of-stock' | 'unknown';
/** Physical variant quantity takes precedence over Shopify backorder sellability. */
export function signatureCardStock(hit: any): SignatureCardStock {
  const raw = hit?.inventory_quantity;
  if ((typeof raw === 'number' || (typeof raw === 'string' && raw.trim())) && Number.isFinite(Number(raw))) {
    return Number(raw) > 0 ? 'in-stock' : 'out-of-stock';
  }
  return hit?.inventory_available === true ? 'in-stock' : hit?.inventory_available === false ? 'out-of-stock' : 'unknown';
}

export type SignatureCardEligibility = { kind: 'add' | 'options' | 'appointment' | 'inquiry'; variantId: string };
export function signatureCardEligibility(hit: any, productUrl: string, path = ''): SignatureCardEligibility {
  const variantId = signatureVariantId(hit?.objectID);
  const tags = tagsOf(hit);
  if (!hit || isProtectedCardProduct(hit, path, productUrl) || !isWatchType(hit.product_type)
    || signatureCardStock(hit) !== 'in-stock') return { kind: 'inquiry', variantId: '' };
  if (tags.includes('hide-price') || !online(hit)) return { kind: 'appointment', variantId: '' };
  // Conflicting online/sellability evidence is not proof of an in-store policy.
  if (hit.inventory_available !== true || productCardCTA(hit, productUrl, path).inquiry) return { kind: 'inquiry', variantId: '' };
  const options = Array.isArray(hit.option_names) ? hit.option_names.map(name => String(name).toLowerCase()) : null;
  if (!variantId || !options || options.length > 1 || options.some(name => /size|taille/.test(name)) || tags.some(tag => /^(?:resizable|resizeable)$/.test(tag))) {
    return { kind: 'options', variantId: '' };
  }
  return { kind: 'add', variantId };
}

/** The customer reviews the product first, then chooses its existing manual form. */
function signaturePdpUrl(productUrl: string, locale: string): string {
  const url = new URL(productUrl, 'https://native.invalid');
  url.searchParams.set('lang', locale === 'fr' ? 'fr' : 'en');
  url.hash = '';
  return url.pathname + url.search;
}

function signatureSecondary(eligibility: SignatureCardEligibility, url: string, labels: Record<string, string>): string {
  const e = escapeMarkup;
  if (eligibility.kind === 'add') return `<button type="button" class="rf-signature-bag js-signature-add" data-id="${e(eligibility.variantId)}"><span data-signature-bag-label>${e(labels.add)}</span></button>`;
  const label = eligibility.kind === 'appointment' ? labels.appointment : eligibility.kind === 'options' ? labels.options : labels.inquire;
  return `<a class="rf-signature-secondary-link" href="${e(url)}">${e(label)}</a>`;
}

function signatureAvailabilityPrice(hit: any, eligibility: SignatureCardEligibility, markup: string, labels: Record<string, string>): string {
  const label = eligibility.kind === 'appointment' ? labels.inStoreOnly
    : signatureCardStock(hit) === 'out-of-stock' ? labels.outOfStock : '';
  if (!label) return markup;
  return markup.replace(/<\/p>\s*$/i, `<span class="rf-signature-availability" data-signature-availability>${escapeMarkup(label)}</span></p>`);
}

/** Presentation only: keep the catalogue title and model distinctions intact. */
export function signatureCardDisplay(hit: any): { title: string; family: string } {
  const brand = String(hit?.named_tags?.brand || hit?.vendor || '').trim();
  const full = String(hit?.title || '').trim();
  let title = full;
  if (brand && title.toLowerCase().startsWith(brand.toLowerCase() + ' ')) title = title.slice(brand.length).trim();
  let family = '';
  if (/^omega$/i.test(brand)) {
    // Only complete technical suffixes observed in the English/French catalogue
    // are removed. Keep model, anniversary and edition distinctions intact.
    const french = /^Montre\s+OMEGA\s+/i.test(title);
    const modelTitle = french ? title.replace(/^Montre\s+OMEGA\s+/i, '') : title;
    const match = french
      ? /^(.*?)\s+Co-Axial\s+(?:Master\s+)?Chronometer\s+(?:(Chronographe)\s+)?\d{2}(?:[.,]\d+)?\s?mm(?:\s+([ÉE]dition\s+007))?$/i.exec(modelTitle)
      : /^(.*?)\s+Co-Axial\s+(?:Master\s+)?Chronometer\s+(?:(Chronograph)\s+)?\d{2}(?:[.,]\d+)?\s?mm(?:\s+(007 Edition))?\s+Watch$/i.exec(title);
    if (match && match[1].trim()) {
      title = match[1].trim() + (match[3] ? ' ' + match[3] : '');
      family = match[2] || '';
      if (/^Speedmaster Moonwatch Professional$/i.test(title)) {
        title = 'Speedmaster Moonwatch'; family = 'Professional';
      }
    }
  }
  return { title: title || full, family };
}

type SignaturePreview = {
  handle: string; objectId: string; queryId: string; variantId: string; brand: string; title: string; reference: string; url: string;
  image: { url: string; srcSet: string }; titleMarkup: string; metaMarkup: string; priceMarkup: string; secondaryMarkup: string;
};

function signaturePreview(hit: any, url: string, image: { url: string; srcSet: string }, priceMarkup: string,
  size: string, locale: string, labels: Record<string, string>, path: string): SignaturePreview {
  const e = escapeMarkup;
  url = signaturePdpUrl(url, locale);
  const display = signatureCardDisplay(hit);
  const eligibility = signatureCardEligibility(hit, url, path);
  const full = String(hit.title || '');
  const reference = String(hit.sku || '').trim();
  return {
    handle: String(hit.handle || ''), objectId: String(hit.objectID || ''), queryId: String(hit.__queryID || ''), variantId: signatureVariantId(hit.objectID),
    brand: String(hit.named_tags?.brand || hit.vendor || ''), title: full, reference, url, image,
    titleMarkup: `<a href="${e(url)}" title="${e(full)}" aria-label="${e(full)}">${e(display.title)}</a>${display.family ? `<span class="rf-signature-family">${e(display.family)}</span>` : ''}`,
    metaMarkup: `${size ? `<span>${e(size)}</span>` : ''}${reference ? `<span class="rf-native-watch-reference">${locale === 'fr' ? 'Réf.' : 'Ref.'} ${e(reference)}</span>` : ''}`,
    priceMarkup: signatureAvailabilityPrice(hit, eligibility, priceMarkup, labels),
    secondaryMarkup: signatureSecondary(eligibility, url, labels),
  };
}

function signatureSize(hit: any): string {
  const text = [hit.title, hit.meta?.custom?.title_internal].filter(Boolean).join(' ');
  const mm = /(?:^|[^A-Za-z0-9.,])(\d{2}(?:[.,]\d+)?)\s?mm\b/i.exec(text);
  if (mm) return `${mm[1]} mm`;
  const bare = /(?:^|\s)([2-4]\d|50)(?:\s|$)/.exec(text);
  return bare ? `${bare[1]} mm` : '';
}

/** All preview data is built during the existing sibling lookup; hovering never fetches or mutates a cart. */
export function signatureWatchOptions(hit: any, siblings: any[], locale: string, labels: Record<string, string>,
  thumbnail: (image: string) => string, price: (item: any) => string,
  image: (url: string) => { url: string; srcSet: string }): string {
  return nativeWatchCardOptions(hit, siblings, locale, thumbnail, (item, url) => {
    if (typeof item.price !== 'number' || !Number.isFinite(item.price) || item.price < 0) return '';
    const preview = signaturePreview(item, url, image(item.image), price(item), signatureSize(item), locale, labels, '');
    return ` data-signature-preview="${escapeMarkup(JSON.stringify(preview))}"`;
  });
}

/** Atomically update every customer-visible and purchase identity on one card. */
export function applySignatureCardPreview(card: HTMLElement, preview: SignaturePreview): boolean {
  if (card.dataset.signaturePending === 'true' || !preview || !preview.handle || !signatureVariantId(preview.objectId)
    || signatureVariantId(preview.objectId) !== preview.variantId || isProtectedCardProduct({ handle: preview.handle, brand: preview.brand }, '', preview.url)) return false;
  const photo = card.querySelector<HTMLAnchorElement>('.rf-native-watch-photo');
  const img = photo?.querySelector<HTMLImageElement>('img');
  const title = card.querySelector<HTMLElement>('.product-card__title');
  const meta = card.querySelector<HTMLElement>('.product-card__spec-line');
  const oldPrice = card.querySelector<HTMLElement>('.product-card__price');
  const secondary = card.querySelector<HTMLElement>('.rf-signature-secondary');
  const view = card.querySelector<HTMLAnchorElement>('.rf-watch-shop');
  if (!photo || !img || !title || !meta || !oldPrice || !secondary || !view) return false;
  card.dataset.product = preview.handle; card.dataset.objectId = preview.objectId; card.dataset.brand = preview.brand;
  // Related lookups intentionally do not create an Algolia analytics query.
  card.dataset.queryId = preview.queryId || '';
  photo.href = preview.url; img.src = preview.image.url; img.dataset.src = preview.image.url;
  img.srcset = preview.image.srcSet; img.dataset.srcset = preview.image.srcSet;
  title.innerHTML = preview.titleMarkup; meta.innerHTML = preview.metaMarkup; oldPrice.outerHTML = preview.priceMarkup;
  const brand = card.querySelector<HTMLElement>('.product-card__brand-line'); if (brand) brand.textContent = preview.brand;
  secondary.innerHTML = preview.secondaryMarkup + '<p class="rf-signature-status" data-signature-status role="status" aria-live="polite" hidden></p>';
  view.href = preview.url;
  // Retain the localized action label while refreshing its exact accessible product name.
  view.setAttribute('aria-label', `${view.textContent?.trim() || ''} — ${preview.title}`);
  const identity = card.querySelector<HTMLElement>('[data-watch-tryon]');
  if (identity) Object.assign(identity.dataset, { watchId: `${preview.handle}:${preview.objectId}`, watchTitle: preview.title,
    watchBrand: preview.brand, watchSku: preview.reference, watchImage: preview.image.url, watchUrl: preview.url });
  card.querySelectorAll<HTMLAnchorElement>('[data-signature-preview]').forEach(link => {
    let selected = false; try { selected = JSON.parse(link.dataset.signaturePreview || '{}').handle === preview.handle; } catch { /* Leave malformed links navigable. */ }
    if (selected) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current');
  });
  return true;
}

/** Keep the last complete choice selected as the pointer moves to its purchase action.
 * Delegation survives filtering/pagination; touch links retain exact product navigation. */
export function initSignatureCardPreviews(doc: Document = document): void {
  if (!signatureSansEnabled(doc)) return;
  const root = doc.querySelector<HTMLElement>('main[data-signature-sans-cards][data-watch-buying="collection"]');
  if (!root || root.dataset.signaturePreviewBound === 'true') return;
  root.dataset.signaturePreviewBound = 'true';
  const preview = (target: EventTarget | null) => {
    const link = (target as Element)?.closest<HTMLElement>('[data-signature-preview]');
    const card = link?.closest<HTMLElement>('[data-signature-card]');
    if (!link || !card || !root.contains(card)) return;
    try { applySignatureCardPreview(card, JSON.parse(link.dataset.signaturePreview || '{}')); } catch { /* Native link remains usable. */ }
  };
  root.addEventListener('pointerover', event => { if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) preview(event.target); });
  root.addEventListener('focusin', event => preview(event.target));
}

export function signatureWatchCard(hit: any, productUrl: string, image: { url: string; srcSet: string }, priceMarkup: string,
  optionsMarkup: string, size: string, locale: string, labels: Record<string, string>, path: string, extraClass = ''): string {
  const e = escapeMarkup;
  // Keep this card's language explicit, including when a previous language cookie differs.
  productUrl = signaturePdpUrl(productUrl, locale);
  const display = signatureCardDisplay(hit);
  const eligibility = signatureCardEligibility(hit, productUrl, path);
  const brand = hit.named_tags?.brand || hit.vendor || '';
  const full = String(hit.title || '');
  const reference = String(hit.sku || '').trim();
  const second = signatureSecondary(eligibility, productUrl, labels);
  priceMarkup = signatureAvailabilityPrice(hit, eligibility, priceMarkup, labels);
  // Save/compare already reads this native identity contract. Keep its metadata
  // without exposing a third purchase action or an invisible focusable control.
  const identity = `<span hidden data-watch-tryon data-watch-id="${e(`${hit.handle}:${hit.objectID}`)}" data-watch-title="${e(full)}" data-watch-brand="${e(brand)}" data-watch-sku="${e(reference)}" data-watch-image="${e(image.url)}" data-watch-url="${e(productUrl)}"></span>`;
  return `<li class="collection__grid-item rf-native-watch-item rf-signature-item${extraClass}">
    <article class="product-card rf-native-watch-card rf-signature-card js-product" data-watch-card data-atelier-card data-signature-card
      data-product="${e(hit.handle)}" data-brand="${e(brand)}" data-position="${e(hit.__position)}" data-query-id="${e(hit.__queryID)}" data-object-id="${e(hit.objectID)}">
      <a href="${e(productUrl)}" class="product-card__images rf-native-watch-photo" tabindex="-1" aria-hidden="true"><img class="product-card__img product-card__img--primary lazyload" alt="" data-src="${e(image.url)}" data-srcset="${e(image.srcSet)}" decoding="async" /></a>
      <div class="product-card__details rf-native-watch-body">
        <p class="product-card__brand-line">${e(brand)}</p>
        <h3 class="product-card__title"><a href="${e(productUrl)}" title="${e(full)}" aria-label="${e(full)}">${e(display.title)}</a>${display.family ? `<span class="rf-signature-family">${e(display.family)}</span>` : ''}</h3>
        <p class="product-card__spec-line rf-native-watch-meta">${size ? `<span>${e(size)}</span>` : ''}${reference ? `<span class="rf-native-watch-reference">${locale === 'fr' ? 'Réf.' : 'Ref.'} ${e(reference)}</span>` : ''}</p>
        ${optionsMarkup}
        ${priceMarkup}
        <div class="rf-watch-card-actions rf-signature-actions"><a class="rf-watch-shop" href="${e(productUrl)}" aria-label="${e(labels.view + ' — ' + full)}">${e(labels.view)}</a><div class="rf-signature-secondary">${second}<p class="rf-signature-status" data-signature-status role="status" aria-live="polite" hidden></p></div></div>
      </div>${identity}
    </article></li>`;
}

export type SignatureCartDependencies = {
  fetchCart: () => Promise<any>;
  fetchProduct: (handle: string) => Promise<any>;
  fetchStock: (variantId: string) => Promise<any>;
  addItem: (cartId: string, variantId: string) => Promise<any>;
  isCurrent: () => boolean;
};

/** Confirm the exact variant against fresh product, physical stock and cart. */
export async function runSignatureCardAdd(selection: { handle: string; variantId: string }, deps: SignatureCartDependencies): Promise<{ cart: any; line: any }> {
  const variantId = signatureVariantId(selection.variantId);
  if (!variantId || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(selection.handle) || isProtectedCardProduct({ handle: selection.handle })) throw new Error('Invalid selection');
  const [product, inventory, before] = await Promise.all([deps.fetchProduct(selection.handle), deps.fetchStock(variantId), deps.fetchCart()]);
  if (!deps.isCurrent()) throw new Error('Selection changed');
  const variant = Array.isArray(product?.variants) && product.variants.find(item => item.id === variantId);
  // The locked wrapper omits availableForSale when formatting variants.
  // A missing field still requires positive authoritative stock below; explicit
  // false or malformed availability remains a hard rejection.
  if (product?.handle !== selection.handle || isProtectedCardProduct(product, selection.handle) || !variant
    || (variant.availableForSale !== undefined && variant.availableForSale !== true)
    || !Array.isArray(variant.selectedOptions) || variant.selectedOptions.length > 1
    || variant.selectedOptions.some(option => /size|taille/i.test(String(option?.name || '')))
    || !Array.isArray(inventory) || !inventory.some(location => typeof location?.available === 'number' && Number.isFinite(location.available) && location.available > 0)
    || !before?.id || !Array.isArray(before.lineItems)) throw new Error('Availability unavailable');
  // The collection index can lag a store-only policy change. The fresh
  // product must still explicitly permit online sales before any mutation.
  if (!Array.isArray(product.tags) || !product.tags.some(tag => /^available:\s*online$/i.test(String(tag).trim()))
    || product.tags.some(tag => /^(?:inquiry-only|hide-price|resizable|resizeable)$/i.test(String(tag).trim()))) throw new Error('Unavailable product');
  const quantity = cart => cart.lineItems.filter(line => line.variant?.id === variantId).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const previous = quantity(before);
  if (!Number.isSafeInteger(previous) || previous < 0) throw new Error('Invalid cart quantity');
  const after = await deps.addItem(before.id, variantId);
  if (!after?.id || after.id !== before.id || !Array.isArray(after.lineItems)) throw new Error('Cart did not confirm');
  const line = after.lineItems.find(item => item.variant?.id === variantId && Number(item.quantity) > 0);
  if (!line || !Number.isFinite(previous) || quantity(after) !== previous + 1) throw new Error('Cart did not confirm exact quantity');
  return { cart: after, line };
}
